import { SWORD_POWER, TIERS } from './crafting.js';
// XP + ranks, the Locker catalogue, daily rewards and badges (achievements).
import { WORLDS } from './themes.js';
import { TOPICS } from './questions.js';

export function xpNeeded(level) {
  return 100 + (level - 1) * 35;
}
export function levelInfo(xp) {
  let level = 1, rest = xp;
  while (rest >= xpNeeded(level) && level < 100) {
    rest -= xpNeeded(level);
    level++;
  }
  const need = xpNeeded(level);
  return { level, into: rest, need, pct: Math.min(1, rest / need) };
}

// The top rank carries the player's name: {name} SUPREME becomes AVA SUPREME.
export const RANKS = [
  [1, 'Noob', '#9aa4b2'],
  [3, 'Rookie', '#7be35a'],
  [6, 'Explorer', '#3bceac'],
  [10, 'Builder', '#4d9de0'],
  [14, 'Miner', '#c08a50'],
  [18, 'Adventurer', '#ff8c42'],
  [23, 'Warrior', '#ff5a5a'],
  [28, 'Knight', '#4fe0e0'],
  [34, 'Pro', '#b15eff'],
  [40, 'Champion', '#ffd23f'],
  [47, 'Master', '#ff4d9d'],
  [55, 'Legend', '#ffb020'],
  [65, 'Mythic', '#ff2bd6'],
  [80, 'Ultra Legend', '#22e8ff'],
  [100, '{name} SUPREME', '#ffffff'],
];
export function rankFor(level) {
  let r = RANKS[0];
  for (const x of RANKS) if (level >= x[0]) r = x;
  return { name: r[1], color: r[2], min: r[0] };
}
export function nextRank(level) {
  for (const x of RANKS) if (x[0] > level) return { name: x[1], at: x[0], color: x[2] };
  return null;
}

// ---------------- Locker catalogue ----------------
// price in coins unless cur:'gems'. unlock: 'level:N' | 'boss:W' | 'stars:N' | 'topics:N' (top level in N topics).
// Names may say {name} (withName).
export const CATALOG = {
  skin: [
    { id: 'classic', name: 'Classic {name}', price: 0, desc: 'The original hero.' },
    { id: 'hoodie', name: 'Cool Hoodie', price: 250, desc: 'Comfy and cool.' },
    { id: 'gamer', name: 'Pro Gamer', price: 450, acc: 'headphones', desc: 'Comes with RGB headphones!' },
    { id: 'ninja', name: 'Shadow Ninja', price: 700, desc: 'Silent but deadly fast.' },
    { id: 'astro', name: 'Astronaut', price: 900, desc: 'Ready for space.' },
    { id: 'ice', name: 'Frost Ranger', price: 1100, desc: 'Cool as ice.' },
    { id: 'knight', name: 'Diamond Knight', price: 1500, desc: 'Full diamond armour!' },
    { id: 'lava', name: 'Lava Lord', price: 2000, desc: 'Glowing lava cracks.' },
    { id: 'robot', name: 'Mecha {name}', price: 30, cur: 'gems', acc: 'antenna', desc: 'Beep boop. Glowing visor.' },
    { id: 'galaxy', name: 'Galaxy Hero', price: 45, cur: 'gems', desc: 'Made of actual stars.' },
    { id: 'gold', name: 'Golden King', price: 60, cur: 'gems', acc: 'crown', desc: 'A real crown!' },
    { id: 'rainbow', name: 'Rainbow Legend', unlock: 'level:15', desc: 'Reach Level 15 to unlock.' },
    { id: 'glitch', name: 'Glitch Hunter', unlock: 'boss:5', desc: 'Defeat the Glitch King.' },
  ],
  sword: [
    { id: 'wood', name: 'Wooden Sword', price: 0 },
    { id: 'stone', name: 'Stone Sword', price: 150 },
    { id: 'iron', name: 'Iron Sword', price: 400 },
    { id: 'gold', name: 'Gold Sword', price: 800 },
    { id: 'diamond', name: 'Diamond Sword', price: 1400 },
    { id: 'ruby', name: 'Ruby Sword', price: 1800 },
    { id: 'lightning', name: 'Lightning Blade', price: 25, cur: 'gems' },
    { id: 'rainbow', name: 'Rainbow Blade', price: 40, cur: 'gems' },
    { id: 'ice', name: 'Frost Blade', unlock: 'boss:3', desc: 'Beat the Ice Titan.' },
    { id: 'lava', name: 'Magma Blade', unlock: 'boss:4', desc: 'Beat the Magma King.' },
    { id: 'glitch', name: 'Glitch Blade', unlock: 'boss:5', desc: 'Beat the Glitch King.' },
    { id: 'crystal', name: 'Crystal Blade', price: 0, unlock: 'topics:3', desc: 'Reach the top level in 3 topics.' },
  ],
  trail: [
    { id: 'none', name: 'No Trail', price: 0 },
    { id: 'sparkle', name: 'Sparkles', price: 300 },
    { id: 'notes', name: 'Music Notes', price: 450 },
    { id: 'hearts', name: 'Hearts', price: 450 },
    { id: 'fire', name: 'Fire', price: 900 },
    { id: 'diamond', name: 'Diamond Dust', price: 1200 },
    { id: 'rainbow', name: 'Rainbow', price: 20, cur: 'gems' },
    { id: 'lightning', name: 'Lightning', price: 30, cur: 'gems' },
    { id: 'glitch', name: 'Glitch', unlock: 'boss:5', desc: 'Defeat the Glitch King.' },
  ],
  pet: [
    { id: 'none', name: 'No Pet', price: 0 },
    { id: 'dog', name: 'Scout the Dog', price: 0, desc: 'Your free learning buddy. Loves exploring!', magnet: 2.8 },
    { id: 'slime', name: 'Slime Buddy', price: 600, desc: 'Grabs coins near you.', magnet: 2.6 },
    { id: 'drone', name: 'Robo Drone', price: 1000, desc: 'Bigger coin magnet!', magnet: 3.6 },
    { id: 'ghost', name: 'Ghost Block', price: 1400, desc: 'Spooky coin magnet.', magnet: 3.2 },
    { id: 'golem', name: 'Mini Golem', price: 1800, desc: '+1 extra heart ❤️', magnet: 2.4, heart: 1 },
    { id: 'lucky', name: 'Lucky Cube', price: 30, cur: 'gems', desc: '+20% more coins!', magnet: 2.6, coinBoost: 0.2 },
    { id: 'dragon', name: 'Baby Dragon', price: 50, cur: 'gems', desc: 'Hatch in Explore & Craft, or buy with gems. Huge magnet + extra heart!', magnet: 4.5, heart: 1 },
    { id: 'tnt', name: 'Boomy', unlock: 'boss:2', desc: 'Beat the Sandstone Golem.', magnet: 3 },
  ],
};

// Weapons have gameplay strength, and advanced purchases need learning as well as treasure.
const swordCosts={stone:180,iron:650,gold:1100,diamond:3500,ruby:8000};
for(const it of CATALOG.sword){
  it.learn=TIERS[it.id]?.learn || ({gold:60,lightning:100,rainbow:160}[it.id]||0);
  if(swordCosts[it.id])it.price=swordCosts[it.id];
  // Unlockable swords keep their own unlock line (Beat the Ice Titan., Reach the top level in 3 topics.).
  it.desc=`Power ${SWORD_POWER[it.id]}${it.learn?` · ${it.learn} learned answers`:''}. Stronger against animals and adventure monsters.${it.unlock?' '+(it.desc||''):''}`;
}

export function catalogItem(cat, id) {
  return CATALOG[cat].find((x) => x.id === id);
}

export function unlockMet(save, unlock) {
  if (!unlock) return true;
  const [k, v] = unlock.split(':');
  const n = parseInt(v, 10);
  if (k === 'level') return levelInfo(save.xp).level >= n;
  if (k === 'boss') return !!save.levels[`${n}-B`]?.done;
  if (k === 'stars') return totalStars(save) >= n;
  if (k === 'topics') return topicsAtMax(save) >= n;
  return false;
}

// How many learning topics have reached their top level. `best` keeps a top level earned after a later slip.
export function topicsAtMax(save) {
  let n = 0;
  for (const [t, spec] of Object.entries(TOPICS)) {
    const st = save.topics?.[t];
    if (st && Math.max(Number(st.lvl) || 0, Number(st.best) || 0) >= spec.max) n++;
  }
  return n;
}

export function totalStars(save) {
  let s = 0;
  for (const v of Object.values(save.levels)) s += (v.stars || []).filter(Boolean).length;
  return s;
}

export function worldStars(save, w) {
  let s = 0;
  for (const l of ['1', '2', '3', 'B']) s += (save.levels[`${w}-${l}`]?.stars || []).filter(Boolean).length;
  return s;
}

export function isLevelUnlocked(save, id) {
  if (save.settings?.unlockAll) return true;
  if (id === '1-1') return true;
  const [w, l] = id.split('-');
  const wi = parseInt(w, 10);
  if (l === '1') return !!save.levels[`${wi - 1}-B`]?.done;
  const prev = l === 'B' ? `${wi}-3` : `${wi}-${parseInt(l, 10) - 1}`;
  return !!save.levels[prev]?.done;
}

// ---------------- Daily rewards ----------------
export const DAILY = [
  { coins: 50 },
  { coins: 75 },
  { gems: 5 },
  { coins: 120 },
  { coins: 150 },
  { gems: 10 },
  { coins: 300, gems: 20, mega: true },
];

// ---------------- Badges ----------------
const S = (s) => s.stats;
export const BADGES = [
  { id: 'first_steps', name: 'First Steps', desc: 'Finish your first level', icon: '👟', reward: { coins: 50 }, test: (s) => S(s).levelsDone >= 1 },
  { id: 'pocket_money', name: 'Pocket Money', desc: 'Collect 100 coins', icon: '🪙', reward: { coins: 25 }, test: (s) => S(s).coinsTotal >= 100 },
  { id: 'coin_tycoon', name: 'Coin Tycoon', desc: 'Collect 1,000 coins', icon: '💰', reward: { gems: 5 }, test: (s) => S(s).coinsTotal >= 1000 },
  { id: 'mega_rich', name: 'Mega Rich', desc: 'Collect 5,000 coins', icon: '🏦', reward: { gems: 15 }, test: (s) => S(s).coinsTotal >= 5000 },
  { id: 'gem_hunter', name: 'Gem Hunter', desc: 'Collect 25 gems', icon: '💎', reward: { coins: 100 }, test: (s) => S(s).gemsTotal >= 25 },
  { id: 'on_fire', name: 'On Fire!', desc: '5 right answers in a row', icon: '🔥', reward: { coins: 50 }, test: (s) => S(s).bestStreak >= 5 },
  { id: 'unstoppable', name: 'Unstoppable', desc: '10 right answers in a row', icon: '⚡', reward: { gems: 5 }, test: (s) => S(s).bestStreak >= 10 },
  { id: 'brain_legend', name: 'Brain Legend', desc: '25 right answers in a row', icon: '🧠', reward: { gems: 15 }, test: (s) => S(s).bestStreak >= 25 },
  { id: 'smart_cookie', name: 'Smart Cookie', desc: 'Answer 50 questions right', icon: '🍪', reward: { coins: 100 }, test: (s) => S(s).correct >= 50 },
  { id: 'big_brain', name: 'Big Brain', desc: 'Answer 250 questions right', icon: '🎓', reward: { gems: 10 }, test: (s) => S(s).correct >= 250 },
  { id: 'genius', name: 'Genius Mode', desc: 'Answer 1,000 questions right', icon: '🏅', reward: { gems: 30 }, test: (s) => S(s).correct >= 1000 },
  { id: 'perfect', name: 'Perfect Brain', desc: 'Get every question right first time in a level', icon: '💯', reward: { gems: 5 }, test: (s) => S(s).perfect >= 1 },
  { id: 'superstar', name: 'Superstar', desc: 'Get 3 stars on a level', icon: '🌟', reward: { coins: 75 }, test: (s) => S(s).threeStars >= 1 },
  { id: 'star_collector', name: 'Star Collector', desc: 'Earn 30 stars', icon: '✨', reward: { gems: 10 }, test: (s) => totalStars(s) >= 30 },
  { id: 'explorer', name: 'Explorer', desc: 'Find 5 secret stars', icon: '🧭', reward: { gems: 5 }, test: (s) => S(s).secrets >= 5 },
  { id: 'slime_slayer', name: 'Slime Slayer', desc: 'Defeat King Slime', icon: '👑', reward: { gems: 5 }, test: (s) => !!s.levels['1-B']?.done },
  { id: 'golem_crusher', name: 'Golem Crusher', desc: 'Defeat the Sandstone Golem', icon: '🗿', reward: { gems: 5 }, test: (s) => !!s.levels['2-B']?.done },
  { id: 'titan_tamer', name: 'Titan Tamer', desc: 'Defeat the Ice Titan', icon: '🧊', reward: { gems: 5 }, test: (s) => !!s.levels['3-B']?.done },
  { id: 'magma_master', name: 'Magma Master', desc: 'Defeat the Magma King', icon: '🌋', reward: { gems: 5 }, test: (s) => !!s.levels['4-B']?.done },
  { id: 'glitch_destroyer', name: 'Glitch Destroyer', desc: 'Defeat the Glitch King and save the Block Realm!', icon: '🏆', reward: { gems: 50 }, test: (s) => !!s.levels['5-B']?.done },
  { id: 'monster_basher', name: 'Monster Basher', desc: 'Defeat 25 monsters', icon: '⚔️', reward: { coins: 100 }, test: (s) => S(s).mobs >= 25 },
  { id: 'stomp_champ', name: 'Stomp Champion', desc: 'Stomp on 10 monsters', icon: '🦶', reward: { coins: 75 }, test: (s) => S(s).stomps >= 10 },
  { id: 'feeling_lucky', name: 'Feeling Lucky', desc: 'Break 10 Lucky Blocks', icon: '🍀', reward: { coins: 100 }, test: (s) => S(s).lucky >= 10 },
  { id: 'blitz_pro', name: 'Blitz Pro', desc: 'Score 1,500 in Brain Blitz', icon: '⏱️', reward: { gems: 5 }, test: (s) => (s.blitz?.best || 0) >= 1500 },
  { id: 'blitz_legend', name: 'Blitz Legend', desc: 'Score 5,000 in Brain Blitz', icon: '🚀', reward: { gems: 20 }, test: (s) => (s.blitz?.best || 0) >= 5000 },
  { id: 'table_titan', name: 'Times Table Titan', desc: 'Master a whole times table (1–12)', icon: '✖️', reward: { gems: 10 }, test: (s, q) => q && [2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12].some((t) => q.tableMastered(t)) },
  { id: 'table_emperor', name: 'Times Table Emperor', desc: 'Master ALL the times tables 2–12', icon: '🧮', reward: { gems: 50 }, test: (s, q) => q && [2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12].every((t) => q.tableMastered(t)) },
  { id: 'loyal', name: 'Loyal Hero', desc: 'Play 3 days in a row', icon: '📅', reward: { gems: 5 }, test: (s) => (s.daily?.bestStreak || 0) >= 3 },
  { id: 'super_fan', name: 'Super Fan', desc: 'Play 7 days in a row', icon: '🔥', reward: { gems: 20 }, test: (s) => (s.daily?.bestStreak || 0) >= 7 },
  { id: 'fresh_fit', name: 'Fresh Fit', desc: 'Get a new skin', icon: '👕', reward: { coins: 50 }, test: (s) => s.owned.skin.length >= 2 },
  { id: 'best_buddy', name: 'Best Buddy', desc: 'Get a pet', icon: '🐾', reward: { coins: 50 }, test: (s) => s.owned.pet.length >= 2 },
  { id: 'rising_star', name: 'Rising Star', desc: 'Reach Level 10', icon: '📈', reward: { gems: 5 }, test: (s) => levelInfo(s.xp).level >= 10 },
  { id: 'pro_player', name: 'Pro Player', desc: 'Reach Level 25', icon: '🎮', reward: { gems: 15 }, test: (s) => levelInfo(s.xp).level >= 25 },
  { id: 'untouchable', name: 'Untouchable', desc: 'Finish a level without losing a heart', icon: '🛡️', reward: { coins: 75 }, test: (s) => S(s).noDamage >= 1 },
  { id: 'speedrunner', name: 'Speedrunner', desc: 'Finish a level in under 2 minutes', icon: '⏩', reward: { coins: 75 }, test: (s) => Object.values(s.levels).some((l) => l.best && l.best < 120) },
  { id: 'treasure', name: 'Treasure Hunter', desc: 'Open 10 treasure chests', icon: '🧰', reward: { gems: 5 }, test: (s) => S(s).chests >= 10 },
  // 1.7: lesson challenges and top topic levels.
  { id: 'challenger', name: 'Challenger', desc: 'Win 10 challenge questions', icon: '🎯', reward: { coins: 100 }, test: (s) => (S(s).challenges || 0) >= 10 },
  { id: 'challenge_master', name: 'Challenge Master', desc: 'Win 50 challenge questions', icon: '🥇', reward: { gems: 15 }, test: (s) => (S(s).challenges || 0) >= 50 },
  { id: 'topic_top', name: 'Top Level', desc: 'Reach the top level in any topic', icon: '🔝', reward: { gems: 10 }, test: (s) => topicsAtMax(s) >= 1 },
  { id: 'all_rounder', name: 'All-Rounder', desc: 'Reach the top level in 6 topics', icon: '🌈', reward: { gems: 30 }, test: (s) => topicsAtMax(s) >= 6 },
];

export function worldName(w) {
  return WORLDS[w - 1]?.name || '';
}
